import { defineComponent, h, useAttrs } from 'vue'
import { VChip } from 'vuetify/components'
import { hasBooleanishAttr } from './legacyInputProps'

const LEGACY_COLOR_NAMES = [
  'red',
  'pink',
  'purple',
  'deep-purple',
  'indigo',
  'blue',
  'light-blue',
  'cyan',
  'teal',
  'green',
  'light-green',
  'lime',
  'yellow',
  'amber',
  'orange',
  'deep-orange',
  'brown',
  'blue-grey',
  'grey'
]

const LEGACY_COLOR_VARIANTS = [
  'lighten-1',
  'lighten-2',
  'lighten-3',
  'lighten-4',
  'lighten-5',
  'darken-1',
  'darken-2',
  'darken-3',
  'darken-4',
  'accent-1',
  'accent-2',
  'accent-3',
  'accent-4'
]

const LIGHT_TEXT_CHIP_COLORS = new Set([
  'primary',
  'secondary',
  'accent',
  'error'
])

VChip.compatConfig = {
  ...(VChip.compatConfig || {}),
  ATTR_ENUMERATED_COERCION: 'suppress-warning',
  COMPONENT_V_MODEL: false
}

function resolveSize(attrs) {
  if (attrs.size != null) return attrs.size
  if (hasBooleanishAttr(attrs['x-small']) || hasBooleanishAttr(attrs.xSmall)) {
    return 'x-small'
  }
  if (hasBooleanishAttr(attrs.small)) return 'small'
  if (hasBooleanishAttr(attrs.large)) return 'large'
  if (hasBooleanishAttr(attrs['x-large']) || hasBooleanishAttr(attrs.xLarge)) {
    return 'x-large'
  }
  return undefined
}

function resolveVariant(attrs) {
  if (attrs.variant != null) return attrs.variant
  if (hasBooleanishAttr(attrs.outlined)) return 'outlined'
  if (attrs.color != null) return 'flat'
  return undefined
}

function normalizeClassList(classValue) {
  if (!classValue) return []
  if (Array.isArray(classValue)) return classValue.flatMap(normalizeClassList)
  if (typeof classValue === 'object') {
    return Object.keys(classValue).filter((className) => classValue[className])
  }
  return String(classValue).split(/\s+/).filter(Boolean)
}

function resolveLegacyColor(attrs) {
  const classList = normalizeClassList(attrs.class)
  const colorClass = classList.find((className) =>
    LEGACY_COLOR_NAMES.includes(className)
  )
  const variantClass = classList.find((className) =>
    LEGACY_COLOR_VARIANTS.includes(className)
  )

  if (colorClass && variantClass) return `${colorClass}-${variantClass}`

  if (typeof attrs.color === 'string') {
    const [colorName, variantName] = attrs.color.split(/\s+/)
    if (
      LEGACY_COLOR_NAMES.includes(colorName) &&
      LEGACY_COLOR_VARIANTS.includes(variantName)
    ) {
      return `${colorName}-${variantName}`
    }
  }

  return undefined
}

function hasLegacyColorClass(attrs) {
  const classList = normalizeClassList(attrs.class)
  return classList.some(
    (className) =>
      LEGACY_COLOR_NAMES.includes(className) ||
      LEGACY_COLOR_VARIANTS.includes(className)
  )
}

function resolveLegacyColorClasses(attrs) {
  const classList = normalizeClassList(attrs.class)

  if (classList.some((className) => LEGACY_COLOR_NAMES.includes(className))) {
    return []
  }

  if (typeof attrs.color !== 'string') return []

  const colorClassList = normalizeClassList(attrs.color)
  const colorName = colorClassList.find((className) =>
    LEGACY_COLOR_NAMES.includes(className)
  )
  const variantName = colorClassList.find((className) =>
    LEGACY_COLOR_VARIANTS.includes(className)
  )

  if (colorName && variantName) {
    return colorClassList
  }

  return []
}

function resolveSemanticTextColor(attrs) {
  if (attrs.textColor != null || attrs['text-color'] != null) return undefined
  if (hasBooleanishAttr(attrs.outlined)) return undefined
  if (typeof attrs.color !== 'string') return undefined

  return LIGHT_TEXT_CHIP_COLORS.has(attrs.color) ? '#fff' : undefined
}

export function normalizeChipAttrs(attrs) {
  const normalized = { ...attrs }

  const size = resolveSize(attrs)
  const variant = resolveVariant(attrs)
  const legacyColor = resolveLegacyColor(attrs)
  const legacyColorClasses = resolveLegacyColorClasses(attrs)
  const legacyColorFromClass = hasLegacyColorClass(attrs)
  const semanticTextColor = resolveSemanticTextColor(attrs)

  if (size) normalized.size = size
  if (variant) normalized.variant = variant
  if (
    legacyColor &&
    !legacyColorFromClass &&
    !legacyColorClasses.length &&
    normalized.color == null
  ) {
    normalized.color = legacyColor
  }
  if (legacyColorClasses.length) {
    normalized.class = [
      attrs.class,
      legacyColorClasses,
      'theme--light',
      size ? `v-size--${size}` : null
    ]
    delete normalized.color
  } else {
    normalized.class = [
      attrs.class,
      'theme--light',
      size ? `v-size--${size}` : null
    ]
  }
  const textColor = attrs.textColor ?? attrs['text-color']
  if (textColor != null) {
    normalized.style = [attrs.style, { color: textColor }]
  } else if (semanticTextColor) {
    normalized.style = [attrs.style, { color: semanticTextColor }]
  }
  if (hasBooleanishAttr(attrs.dense) && normalized.density == null) {
    normalized.density = 'compact'
  }
  if (hasBooleanishAttr(attrs.close) && normalized.closable == null) {
    normalized.closable = true
  }
  if (attrs.draggable === false) normalized.draggable = 'false'

  delete normalized.small
  delete normalized.large
  delete normalized['x-small']
  delete normalized.xSmall
  delete normalized['x-large']
  delete normalized.xLarge
  delete normalized.outlined
  delete normalized.dense
  delete normalized.close
  delete normalized.textColor
  delete normalized['text-color']

  return normalized
}

export default defineComponent({
  name: 'VChipCompat',
  compatConfig: {
    RENDER_FUNCTION: false,
    COMPONENT_V_MODEL: false,
    INSTANCE_ATTRS_CLASS_STYLE: false,
    ATTR_ENUMERATED_COERCION: 'suppress-warning'
  },
  inheritAttrs: false,

  setup(_, { slots }) {
    const attrs = useAttrs()

    return () => h(VChip, normalizeChipAttrs(attrs), slots)
  }
})
